import { create } from "zustand";
import type { MachineEvent } from "@/machines/types/event";

/**
 * Accumulated timeline events per camera, so the live view can poll a TAIL
 * instead of re-fetching its whole window.
 *
 * The live timeline currently re-requests 35 minutes every 5 seconds: 169 KB
 * decoded per machine per poll, of which only the last minute or two can
 * possibly have changed. Measured against the API, a trailing 3-minute window
 * costs 8.5 KB and notices new events just as fast (detection latency 4.9 s
 * either way) — the saving is entirely in what the browser then has to parse,
 * diff and re-render, which is what the idle CPU profile is made of.
 *
 * A tail poll on its own would show three minutes of timeline, so the rows the
 * client already has have to live somewhere. That is this store. It is the same
 * shape as `useVideoLibraryStore`, which already does the job for clips.
 *
 * NOT persisted, deliberately: it is a session-scoped view of a window the
 * backend owns, and stale segments are worse than absent ones.
 */

/** Drop anything older than this, so a shift-long session cannot grow forever. */
const MAX_AGE_MS = 2 * 60 * 60 * 1000;
/** Hard ceiling per camera, whatever the age (a busy machine makes ~13 events/min). */
const MAX_RECORDS = 5_000;

interface EventRecord {
  event: MachineEvent;
  /** `startTime` as epoch ms, parsed once on write. */
  startMs: number;
}

/**
 * Stable identity for an event.
 *
 * Machine events carry `uuid`; some truck-layer rows carry only `id`. Falling
 * back rather than requiring `uuid` is what lets one buffer hold both — and an
 * event whose identity changed between polls would otherwise duplicate itself
 * on the timeline.
 */
export const eventKey = (event: MachineEvent): string =>
  String(event.uuid || event.id);

export interface TimeRange {
  from: number;
  to: number;
}

/** Merge overlapping/adjacent ranges, ascending. Coverage is meaningless unsorted. */
const normalise = (ranges: readonly TimeRange[]): TimeRange[] => {
  const sorted = [...ranges].sort((a, b) => a.from - b.from);
  const out: TimeRange[] = [];
  for (const range of sorted) {
    const last = out[out.length - 1];
    // Touching counts as overlapping: two back-to-back fetches leave no hole.
    if (last && range.from <= last.to) last.to = Math.max(last.to, range.to);
    else out.push({ ...range });
  }
  return out;
};

interface EventBufferState {
  /** Per camera, ascending by `startMs`. */
  devices: Record<string, EventRecord[]>;
  /**
   * Which stretches of time have actually been fetched, per camera.
   *
   * Without this the buffer cannot tell "no events here" from "never asked about
   * here" — and that is exactly the distinction a jump depends on. Jumping to an
   * alert outside what we hold must show its rows as LOADING, not as empty; an
   * empty stretch we have genuinely fetched must show as empty, not as a
   * spinner that never resolves.
   */
  coverage: Record<string, TimeRange[]>;
  /**
   * Fold a response into the buffer.
   *
   * A response is AUTHORITATIVE for the window it covers: rows the store holds
   * inside `[fromMs, toMs]` that the response does not mention have been
   * deleted, and are dropped. That is what makes a periodic full-window
   * reconcile able to propagate edits and deletions the tail can never see —
   * without it, a deleted truck cycle would linger on the timeline until reload.
   *
   * Rows outside the window are left alone, so a 3-minute tail never disturbs
   * the hour behind it.
   */
  mergeWindow: (
    deviceMxid: string,
    fromMs: number,
    toMs: number,
    events: readonly MachineEvent[],
  ) => void;
  /** Events overlapping `[fromMs, toMs]`, ascending by start. */
  getEvents: (deviceMxid: string, fromMs: number, toMs: number) => MachineEvent[];
  /** Everything held for a camera, ascending — for consumers that clip it themselves. */
  getAllEvents: (deviceMxid: string) => MachineEvent[];
  /** Whether `[fromMs, toMs]` has been fetched in full. */
  isCovered: (deviceMxid: string, fromMs: number, toMs: number) => boolean;
  /**
   * The parts of `[fromMs, toMs]` never fetched — what a jump has to request,
   * and what the rows should mark as loading while it is in flight.
   */
  missingRanges: (deviceMxid: string, fromMs: number, toMs: number) => TimeRange[];
  /** Forget a camera (device change, location switch). */
  clearDevice: (deviceMxid: string) => void;
}

const startMsOf = (event: MachineEvent): number => new Date(event.startTime).getTime();

/** Where an event ends, from its duration; falls back to a point in time. */
const endMsOf = (record: EventRecord): number =>
  record.startMs + (record.event.durationSeconds ?? 0) * 1000;

export const useEventBufferStore = create<EventBufferState>()((set, get) => ({
  devices: {},
  coverage: {},

  mergeWindow: (deviceMxid, fromMs, toMs, events) => {
    if (!deviceMxid) return;
    set((state) => {
      const existing = state.devices[deviceMxid] ?? [];
      const cutoff = Date.now() - MAX_AGE_MS;

      // Keep what the response does not speak for: rows outside the window, and
      // rows too old to matter.
      const kept = new Map<string, EventRecord>();
      for (const record of existing) {
        if (record.startMs < cutoff) continue;
        if (record.startMs >= fromMs && record.startMs <= toMs) continue;
        kept.set(eventKey(record.event), record);
      }

      for (const event of events) {
        const startMs = startMsOf(event);
        if (!Number.isFinite(startMs) || startMs < cutoff) continue;
        kept.set(eventKey(event), { event, startMs });
      }

      const merged = [...kept.values()]
        .sort((a, b) => a.startMs - b.startMs)
        // Ascending, so the tail is the newest — drop the oldest first.
        .slice(-MAX_RECORDS);

      // A poll that changed nothing must not notify anyone. Object identity
      // cannot decide that — every poll parses fresh objects — so compare what
      // the timeline actually draws from: which events, when they start, how
      // long they run. Most polls land here, and this is what keeps a 5-second
      // cadence from re-rendering the stack for no reason.
      let rowsIdentical = merged.length === existing.length;
      if (rowsIdentical) {
        for (let i = 0; i < merged.length; i++) {
          const a = merged[i];
          const b = existing[i];
          if (
            a.startMs !== b.startMs ||
            eventKey(a.event) !== eventKey(b.event) ||
            a.event.durationSeconds !== b.event.durationSeconds ||
            a.event.state !== b.event.state
          ) {
            rowsIdentical = false;
            break;
          }
        }
      }

      // Coverage is recorded even when the rows did not change — ESPECIALLY
      // then. A window that came back empty changes nothing about the events,
      // and is exactly the case the UI must not treat as "still loading".
      const priorCoverage = state.coverage[deviceMxid] ?? [];
      const alreadyCovered = priorCoverage.some(
        (range) => range.from <= fromMs && range.to >= toMs,
      );
      if (rowsIdentical && alreadyCovered) return state;

      const nextCoverage = alreadyCovered
        ? priorCoverage
        : normalise([
            // Drop coverage for rows we would have pruned anyway, so a long
            // session does not claim to hold a morning it has forgotten.
            ...priorCoverage.filter((range) => range.to >= cutoff),
            { from: fromMs, to: toMs },
          ]);

      return {
        devices: rowsIdentical ? state.devices : { ...state.devices, [deviceMxid]: merged },
        coverage: { ...state.coverage, [deviceMxid]: nextCoverage },
      };
    });
  },

  isCovered: (deviceMxid, fromMs, toMs) =>
    (get().coverage[deviceMxid] ?? []).some((range) => range.from <= fromMs && range.to >= toMs),

  missingRanges: (deviceMxid, fromMs, toMs) => {
    const covered = get().coverage[deviceMxid] ?? [];
    const gaps: TimeRange[] = [];
    let cursor = fromMs;
    for (const range of covered) {
      if (range.to <= cursor) continue;
      if (range.from >= toMs) break;
      if (range.from > cursor) gaps.push({ from: cursor, to: Math.min(range.from, toMs) });
      cursor = Math.max(cursor, range.to);
      if (cursor >= toMs) break;
    }
    if (cursor < toMs) gaps.push({ from: cursor, to: toMs });
    return gaps;
  },

  getEvents: (deviceMxid, fromMs, toMs) => {
    const records = get().devices[deviceMxid];
    if (!records?.length) return [];
    // Overlap, not containment: an event that started before the window and is
    // still running belongs on screen.
    return records
      .filter((record) => record.startMs <= toMs && endMsOf(record) >= fromMs)
      .map((record) => record.event);
  },

  getAllEvents: (deviceMxid) => (get().devices[deviceMxid] ?? []).map((record) => record.event),

  clearDevice: (deviceMxid) =>
    set((state) => {
      // Coverage without rows is a real state — a window fetched that held no
      // events — so a camera is only unknown when NEITHER map mentions it.
      if (!(deviceMxid in state.devices) && !(deviceMxid in state.coverage)) return state;
      const devices = { ...state.devices };
      const coverage = { ...state.coverage };
      delete devices[deviceMxid];
      delete coverage[deviceMxid];
      return { devices, coverage };
    }),
}));
